import React, { useEffect, useRef, useState } from 'react';
import LatexTextField from '../../../common/LatexTextField';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';
import { Button, IconButton, Checkbox, MenuItem, Select } from '@material-ui/core';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import FileBase from 'react-file-base64';
import CodingEditor from './CodingEditor';
import 'prismjs/themes/prism.css';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike.js';
import 'prismjs/components/prism-javascript.js';
import 'prismjs/components/prism-markup.js';
import 'prismjs/components/prism-jsx.js';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-bash';

import { convertToBase64 } from '../../../common/imageUtils';
import {
  parseImageFieldValue,
  serializeImageFieldValue,
} from '../../../../common/imageFieldUtils';
import {
  IMAGEPRESENTATION_TYPE_ALL,
  IMAGEPRESENTATION_TYPE_FIRST,
  IMAGEPRESENTATION_TYPE_RANDOM,
} from '../../../../constants/flashcardTypes';

const SYNTAX_OPTIONS = ['', 'Javascript', 'JSX', 'C++', 'Bash'];

function reorderImageList(list, fromIndex, toIndex) {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.length ||
    toIndex >= list.length
  ) {
    return list;
  }
  const next = [...list];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
}

const getLanguage = (syntax) => {
  switch (syntax) {
    case 'Javascript': return languages.js;
    case 'JSX': return languages.jsx;
    case 'C++': return languages.cpp;
    case 'Bash': return languages.bash;
    default: return null;
  }
};

const playSound = (base64) => {
  if (!base64) return;
  const snd = new Audio(base64);
  snd.play();
};

const FlashcardformGeneric = ({
  flashcardData,
  setFlashcardData,
  handleSubmit,
  flashcardType,
}) => {
  const typeFields = flashcardType?.fields || [];
  const cardFields = flashcardData.fields || [];

  const imageReorderDrag = useRef({ fieldIndex: null, imgIndex: null });
  const [imageReorderHoverKey, setImageReorderHoverKey] = useState(null);
  const hasImageField = typeFields.some((f) => f.fieldType === 'image');

  const fieldByKey = (key) => cardFields.find((f) => f.key === key);
  /** Prefer index when arrays are parallel so duplicate keys still map to distinct values. */
  const getVal = (idx) => {
    const tf = typeFields[idx];
    if (!tf) return '';
    if (cardFields.length === typeFields.length) {
      return cardFields[idx]?.value ?? '';
    }
    return fieldByKey(tf.key)?.value ?? '';
  };

  const typeFieldsSig = typeFields
    .map((f) => `${f.key}:${f.fieldType}`)
    .join('|');

  useEffect(() => {
    if (typeFields.length === 0) return;

    setFlashcardData((prev) => {
      const prevCardFields = prev.fields || [];

      const keysMatch =
        prevCardFields.length === typeFields.length &&
        typeFields.every((tf, i) => prevCardFields[i]?.key === tf.key);

      if (keysMatch) return prev;

      let newFields;
      if (prevCardFields.length === typeFields.length) {
        newFields = typeFields.map((tf, i) => ({
          key: tf.key,
          value: prevCardFields[i]?.value ?? tf.defaultValue ?? '',
        }));
      } else {
        newFields = typeFields.map((tf) => ({
          key: tf.key,
          value:
            prevCardFields.find((f) => f.key === tf.key)?.value ??
            tf.defaultValue ??
            '',
        }));
      }
      return { ...prev, fields: newFields };
    });
  }, [flashcardType?._id, typeFieldsSig, typeFields.length]);

  const readFieldValueFrom = (fieldsArr, idx) => {
    const tf = typeFields[idx];
    if (!tf) return '';
    if (fieldsArr.length === typeFields.length) {
      return fieldsArr[idx]?.value ?? '';
    }
    return fieldsArr.find((f) => f.key === tf.key)?.value ?? '';
  };

  const updateField = (index, value) => {
    setFlashcardData((prev) => {
      const prevFields = prev.fields || [];

      const newFields = typeFields.map((tf, i) => ({
        key: tf.key,
        value: i === index ? value : readFieldValueFrom(prevFields, i),
      }));

      const header = { ...prev.header };
      const firstQIdx = typeFields.findIndex(
        (f) => f.role === 'question' && f.fieldType === 'text',
      );
      if (index === firstQIdx) {
        header.title = value;
      }

      const solutions = [
        ...(prev.solutions || [
          { text: '', sound: '', infotext: '', correct: false },
        ]),
      ];
      const firstSIdx = typeFields.findIndex(
        (f) =>
          f.role === 'solution' &&
          (f.fieldType === 'text' || f.fieldType === 'code'),
      );
      if (index === firstSIdx && solutions.length > 0) {
        solutions[0] = { ...solutions[0], text: value };
      }
      const firstSoundIdx = typeFields.findIndex(
        (f) => f.role === 'solution' && f.fieldType === 'sound',
      );
      if (index === firstSoundIdx && solutions.length > 0) {
        solutions[0] = { ...solutions[0], sound: value };
        header.soundavailable = !!value;
      }

      return {
        ...prev,
        fields: newFields,
        header,
        solutions,
      };
    });
  };

  const handleImageListPaste = (index) => async (e) => {
    if (!e.clipboardData?.files?.[0]) return;
    const base64 = await convertToBase64(e.clipboardData.files[0]);
    const current = parseImageFieldValue(getVal(index));
    updateField(index, serializeImageFieldValue([...current, base64]));
  };

  const renderTextField = (index, autoFocus) => (
    <Card key={index}>
      <LatexTextField
        value={getVal(index)}
        onChange={(e) => updateField(index, e.target.value)}
        onKeyPress={(e) => {
          if (e.code === 'Enter' && e.ctrlKey) {
            handleSubmit(e);
          }
        }}
        autoFocus={autoFocus}
        fullWidth
        multiline
        maxRows={10}
      />
    </Card>
  );

  const renderCodeField = (index) => {
    const testState = flashcardData.test || [];
    const syntax = flashcardData.syntax || '';
    const lang = getLanguage(syntax);

    const setTestState = ({ test }) => {
      setFlashcardData({ ...flashcardData, test });
    };

    return (
      <Card key={index}>
        <div style={{ marginBottom: 4 }}>
          <Select
            value={syntax}
            displayEmpty
            onChange={(e) =>
              setFlashcardData({ ...flashcardData, syntax: e.target.value })
            }
            style={{ minWidth: 120, marginBottom: 4 }}
          >
            {SYNTAX_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s || '(no highlighting)'}
              </MenuItem>
            ))}
          </Select>
        </div>
        <CodingEditor
          value={getVal(index)}
          onValueChange={(code) => updateField(index, code)}
          highlight={(code) =>
            lang ? highlight(code, lang) : code
          }
          solve={false}
          testState={testState}
          setTestState={setTestState}
          onKeyDown={(e) => {
            if (e.code === 'Enter' && e.ctrlKey) {
              handleSubmit(e);
            }
          }}
          autoFocus
        />
      </Card>
    );
  };

  const renderImageField = (index) => {
    const images = parseImageFieldValue(getVal(index));
    const fieldKey = typeFields[index]?.key ?? String(index);
    const fileInputKey = `img-${flashcardType?._id || 'na'}-${index}-${fieldKey}-${images.length}`;
    const setImages = (next) => updateField(index, serializeImageFieldValue(next));
    const addImage = (base64) => setImages([...images, base64]);
    const removeAt = (imgIdx) =>
      setImages(images.filter((_, j) => j !== imgIdx));

    return (
      <div key={index}>
        <div
          onPaste={handleImageListPaste(index)}
          style={{
            minHeight: images.length ? undefined : 100,
            border: '2px solid rgba(0, 0, 0, 1.0)',
            marginBottom: 4,
            padding: images.length ? 8 : 0,
          }}
        >
          {images.length === 0 ? (
            <div style={{ padding: 16, color: '#888' }}>
              Paste images or use the file picker below. Add several; remove with the button on each
              thumbnail. With more than one image, drag thumbnails by the grip to reorder; order
              matters for First only and All modes in the test.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {images.map((src, imgIdx) => {
                const hoverKey = `${index}-${imgIdx}`;
                const isHover = imageReorderHoverKey === hoverKey;
                const canDrag = images.length > 1;
                return (
                  <div
                    key={`${fieldKey}-img-${imgIdx}`}
                    draggable={canDrag}
                    onDragStart={(e) => {
                      imageReorderDrag.current = { fieldIndex: index, imgIndex: imgIdx };
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', String(imgIdx));
                    }}
                    onDragEnd={() => {
                      imageReorderDrag.current = { fieldIndex: null, imgIndex: null };
                      setImageReorderHoverKey(null);
                    }}
                    onDragOver={(e) => {
                      if (!canDrag) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setImageReorderHoverKey(hoverKey);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const { fieldIndex: fi, imgIndex: src } = imageReorderDrag.current;
                      imageReorderDrag.current = { fieldIndex: null, imgIndex: null };
                      setImageReorderHoverKey(null);
                      if (fi !== index || src === null) return;
                      if (src === imgIdx) return;
                      setImages(reorderImageList(images, src, imgIdx));
                    }}
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                      outline: isHover ? '3px solid #1976d2' : undefined,
                      outlineOffset: 2,
                      cursor: canDrag ? 'grab' : 'default',
                    }}
                  >
                    {canDrag ? (
                      <div
                        aria-hidden
                        title="Drag to reorder"
                        style={{
                          position: 'absolute',
                          left: 4,
                          top: 4,
                          zIndex: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          background: 'rgba(255,255,255,0.92)',
                          borderRadius: 4,
                          pointerEvents: 'none',
                          color: '#555',
                        }}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </div>
                    ) : null}
                    <img
                      src={src}
                      height="300"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      style={{ border: '0px solid rgba(0, 0, 0, 1.0)', display: 'block' }}
                      alt=""
                    />
                    <IconButton
                      size="small"
                      aria-label="Remove image"
                      onClick={() => removeAt(imgIdx)}
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        background: 'rgba(255,255,255,0.9)',
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <FileBase
          key={fileInputKey}
          type="file"
          multiple={false}
          onDone={({ base64 }) => addImage(base64)}
        />
      </div>
    );
  };

  const renderSoundField = (index) => {
    const value = getVal(index);
    const fieldKey = typeFields[index]?.key ?? String(index);
    const fileInputKey = `snd-${flashcardType?._id || 'na'}-${index}-${fieldKey}`;
    return (
      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <VolumeUpIcon
          style={{ fill: value ? 'green' : '', cursor: value ? 'pointer' : 'default' }}
          onClick={() => playSound(value)}
        />
        <FileBase
          key={fileInputKey}
          type="file"
          multiple={false}
          onDone={({ base64 }) => updateField(index, base64)}
        />
      </div>
    );
  };

  const renderDateField = (index) => {
    let dateObj = { format: 'year', start: '', end: '', isrange: false };
    try {
      const parsed = JSON.parse(getVal(index) || '{}');
      dateObj = { ...dateObj, ...parsed };
    } catch {
      /* use defaults */
    }
    const updateDate = (patch) => {
      const updated = { ...dateObj, ...patch };
      updateField(index, JSON.stringify(updated));
    };
    return (
      <Card key={index} style={{ padding: 8 }}>
        <div style={{ marginBottom: 8 }}>
          <label>Format: </label>
          <select
            value={dateObj.format}
            onChange={(e) => updateDate({ format: e.target.value })}
          >
            <option value="year">year</option>
            <option value="year month">year month</option>
            <option value="year month day">year month day</option>
          </select>
          <label style={{ marginLeft: 16 }}>
            <input
              type="checkbox"
              checked={dateObj.isrange}
              onChange={(e) => updateDate({ isrange: e.target.checked })}
            />
            {' '}Range
          </label>
        </div>
        <div>
          <label>Start: </label>
          <input
            type="date"
            value={dateObj.start ? dateObj.start.substring(0, 10) : ''}
            onChange={(e) => updateDate({ start: e.target.value })}
          />
        </div>
        {dateObj.isrange && (
          <div style={{ marginTop: 4 }}>
            <label>End: </label>
            <input
              type="date"
              value={dateObj.end ? dateObj.end.substring(0, 10) : ''}
              onChange={(e) => updateDate({ end: e.target.value })}
            />
          </div>
        )}
      </Card>
    );
  };

  const renderEnumerationField = (index) => {
    const raw = getVal(index);
    const items = raw ? raw.split('\n') : [''];

    const updateItems = (newItems) => {
      updateField(index, newItems.join('\n'));
    };

    const focusEnumItem = (fieldIdx, itemIdx) => {
      setTimeout(() => {
        const container = document.getElementById(`enum-create-${fieldIdx}`);
        if (!container) return;
        const inputs = container.querySelectorAll('input, textarea');
        if (inputs[itemIdx]) inputs[itemIdx].focus();
      }, 0);
    };

    return (
      <Card key={index} style={{ padding: 8 }} id={`enum-create-${index}`}>
        {items.map((item, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ minWidth: 24, textAlign: 'right', color: '#888' }}>{idx + 1}.</span>
            <LatexTextField
              value={item}
              onChange={(e) => {
                const updated = [...items];
                updated[idx] = e.target.value;
                updateItems(updated);
              }}
              fullWidth
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
                  e.preventDefault();
                  const newItems = [...items];
                  newItems.splice(idx + 1, 0, '');
                  updateItems(newItems);
                  focusEnumItem(index, idx + 1);
                }
              }}
            />
            <IconButton
              size="small"
              onClick={() => {
                const updated = items.filter((_, i) => i !== idx);
                updateItems(updated.length ? updated : ['']);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
        <Button size="small" startIcon={<AddCircleIcon />} onClick={() => updateItems([...items, ''])}>
          Add item
        </Button>
      </Card>
    );
  };

  const renderQuizField = (index) => {
    let options = [];
    try {
      options = JSON.parse(getVal(index) || '[]');
    } catch {
      options = [];
    }
    if (!options.length) options = [{ text: '', correct: false }];

    const updateOptions = (newOptions) => {
      updateField(index, JSON.stringify(newOptions));
    };

    return (
      <Card key={index} style={{ padding: 8 }}>
        {options.map((opt, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Checkbox
              checked={opt.correct}
              onChange={(e) => {
                const updated = options.map((o, i) =>
                  i === idx ? { ...o, correct: e.target.checked } : o,
                );
                updateOptions(updated);
              }}
              title="Correct answer"
            />
            <LatexTextField
              value={opt.text}
              onChange={(e) => {
                const updated = options.map((o, i) =>
                  i === idx ? { ...o, text: e.target.value } : o,
                );
                updateOptions(updated);
              }}
              fullWidth
            />
            <IconButton
              size="small"
              onClick={() => {
                const updated = options.filter((_, i) => i !== idx);
                updateOptions(updated.length ? updated : [{ text: '', correct: false }]);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
        <Button
          size="small"
          startIcon={<AddCircleIcon />}
          onClick={() => updateOptions([...options, { text: '', correct: false }])}
        >
          Add option
        </Button>
      </Card>
    );
  };

  const renderDropdownField = (index) => {
    let options = [];
    try {
      options = JSON.parse(getVal(index) || '[]');
    } catch {
      options = [];
    }
    if (!Array.isArray(options) || !options.length) {
      options = [{ text: '', correct: true }];
    }

    const normalizeSingleCorrect = (newOptions) => {
      const firstCorrectIdx = newOptions.findIndex((opt) => !!opt.correct);
      return newOptions.map((opt, idx) => ({
        ...opt,
        correct: firstCorrectIdx >= 0 ? idx === firstCorrectIdx : idx === 0,
      }));
    };

    const updateOptions = (newOptions) => {
      updateField(index, JSON.stringify(normalizeSingleCorrect(newOptions)));
    };

    const focusDropdownItem = (itemIdx) => {
      setTimeout(() => {
        const container = document.getElementById(`dropdown-create-${index}`);
        if (!container) return;
        const inputs = container.querySelectorAll('input, textarea');
        if (inputs[itemIdx]) inputs[itemIdx].focus();
      }, 0);
    };

    return (
      <Card key={index} style={{ padding: 8 }} id={`dropdown-create-${index}`}>
        {options.map((opt, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Checkbox
              checked={!!opt.correct}
              onChange={() => {
                const updated = options.map((o, i) => ({ ...o, correct: i === idx }));
                updateOptions(updated);
              }}
              title="Correct answer"
            />
            <LatexTextField
              value={opt.text}
              onChange={(e) => {
                const updated = options.map((o, i) =>
                  i === idx ? { ...o, text: e.target.value } : o,
                );
                updateOptions(updated);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
                  e.preventDefault();
                  const newOptions = [...options];
                  newOptions.splice(idx + 1, 0, { text: '', correct: false });
                  updateOptions(newOptions);
                  focusDropdownItem(idx + 1);
                }
              }}
              fullWidth
            />
            <IconButton
              size="small"
              onClick={() => {
                const updated = options.filter((_, i) => i !== idx);
                updateOptions(updated.length ? updated : [{ text: '', correct: true }]);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
        <Button
          size="small"
          startIcon={<AddCircleIcon />}
          onClick={() => updateOptions([...options, { text: '', correct: false }])}
        >
          Add option
        </Button>
      </Card>
    );
  };

  const renderImagePosField = (index) => {
    let pos = { x: 0, y: 0, radius: 20, image: '' };
    try {
      pos = { ...pos, ...JSON.parse(getVal(index) || '{}') };
    } catch {
      /* use defaults */
    }
    const updatePos = (patch) => {
      const next = { ...pos, ...patch };
      updateField(index, JSON.stringify(next));
    };

    const imageValue = pos.image || '';

    const handleImagePaste = async (e) => {
      if (!e.clipboardData?.files?.[0]) return;
      const base64 = await convertToBase64(e.clipboardData.files[0]);
      updatePos({ image: base64 });
    };

    const handleImageClick = (e) => {
      const rect = e.target.getBoundingClientRect();
      const x = Math.round(e.clientX - rect.left);
      const y = Math.round(e.clientY - rect.top);
      updatePos({ x, y });
    };

    return (
      <Card key={index} style={{ padding: 8 }}>
        {imageValue ? (
          <div style={{ position: 'relative', display: 'inline-block', cursor: 'crosshair', marginBottom: 8 }}>
            <img src={imageValue} height="300" alt="" onClick={handleImageClick} />
            <div
              style={{
                position: 'absolute',
                left: pos.x - pos.radius,
                top: pos.y - pos.radius,
                width: pos.radius * 2,
                height: pos.radius * 2,
                borderRadius: '50%',
                background: 'rgba(255, 0, 0, 0.16)',
                border: '1px solid rgba(255, 0, 0, 0.4)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: pos.x - 8,
                top: pos.y - 8,
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: 'rgba(255, 0, 0, 0.7)',
                border: '2px solid red',
                pointerEvents: 'none',
              }}
            />
          </div>
        ) : (
          <div
            onPaste={handleImagePaste}
            style={{
              minHeight: 100,
              border: '2px solid rgba(0, 0, 0, 1.0)',
              marginBottom: 8,
              padding: 12,
              color: '#888',
            }}
          >
            Paste an image or use the file picker below
          </div>
        )}
        <FileBase
          key={`imgpos-${flashcardType?._id || 'na'}-${index}-${typeFields[index]?.key ?? index}`}
          type="file"
          multiple={false}
          onDone={({ base64 }) => updatePos({ image: base64 })}
        />
        <div style={{ display: 'flex', gap: 16 }}>
          <div>
            <label>X: </label>
            <input
              type="number"
              value={pos.x}
              onChange={(e) => updatePos({ x: parseFloat(e.target.value) || 0 })}
              style={{ width: 80 }}
            />
          </div>
          <div>
            <label>Y: </label>
            <input
              type="number"
              value={pos.y}
              onChange={(e) => updatePos({ y: parseFloat(e.target.value) || 0 })}
              style={{ width: 80 }}
            />
          </div>
          <div>
            <label>Radius: </label>
            <input
              type="number"
              min="0"
              value={pos.radius}
              onChange={(e) => updatePos({ radius: Math.max(0, parseFloat(e.target.value) || 0) })}
              style={{ width: 80 }}
            />
          </div>
        </div>
      </Card>
    );
  };

  let firstInputRendered = false;

  const renderField = (fieldDef, index) => {
    const isFirstInput =
      !firstInputRendered &&
      (fieldDef.fieldType === 'text' || fieldDef.fieldType === 'code');
    if (isFirstInput) firstInputRendered = true;

    switch (fieldDef.fieldType) {
      case 'text':
        return renderTextField(index, isFirstInput);
      case 'code':
        return renderCodeField(index, isFirstInput);
      case 'image':
        return renderImageField(index);
      case 'sound':
        return renderSoundField(index);
      case 'date':
        return renderDateField(index);
      case 'enumeration':
        return renderEnumerationField(index);
      case 'quiz':
        return renderQuizField(index);
      case 'dropdown':
        return renderDropdownField(index);
      case 'imagepos':
        return renderImagePosField(index);
      default:
        return renderTextField(index, isFirstInput);
    }
  };

  return (
    <div>
      {hasImageField ? (
        <div style={{ marginBottom: 16 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>Question images in test</Grid>
            <Grid item>
              <Select
                value={
                  flashcardData.imagepresentationtype ||
                  IMAGEPRESENTATION_TYPE_RANDOM
                }
                onChange={(e) =>
                  setFlashcardData({
                    ...flashcardData,
                    imagepresentationtype: e.target.value,
                  })
                }
                displayEmpty
                inputProps={{
                  'aria-label': 'How to show multiple question images',
                }}
              >
                <MenuItem value={IMAGEPRESENTATION_TYPE_ALL}>
                  All (in this order)
                </MenuItem>
                <MenuItem value={IMAGEPRESENTATION_TYPE_FIRST}>First only</MenuItem>
                <MenuItem value={IMAGEPRESENTATION_TYPE_RANDOM}>
                  One at random
                </MenuItem>
              </Select>
            </Grid>
          </Grid>
        </div>
      ) : null}
      {typeFields.map((fieldDef, index) => (
        <div key={`${fieldDef.key}-${index}`}>
          <Grid item>
            {fieldDef.label}{' '}
            <span style={{ color: '#888', fontSize: '0.85em' }}>
              ({fieldDef.role})
            </span>
          </Grid>
          <Grid item>{renderField(fieldDef, index)}</Grid>
        </div>
      ))}
    </div>
  );
};

export default FlashcardformGeneric;
